<script setup lang="ts">
import { computed, ref, watch } from 'vue';
import { VaccinationProgressEntry } from '@/types/v2';
import { useRouter } from 'vue-router';
import { useVaccination } from "@/composables/tasks/vaccination/useVaccination";
import { useVaccinationConfigurations } from "@/composables/tasks/vaccination/useVaccinationConfigurations";
import { useRecord } from "@/composables/tasks/vaccination/useVaccinationRecords";

import FooterActions from '@/components/v2/Tasks/Vaccination/Buttons/ActionButtons/FooterActions.vue';
import VaccinationRevisionModal from "@/components/v2/Tasks/Vaccination/Modals/VaccinationRevisionModal.vue";
import { useWeight } from "@/composables/tasks/vaccination/useVaccinationWeight";
import { useTask } from "@/composables/tasks/useTask";
import { TasksService } from "@/services/v2/tasks";
import { useNotifications } from "@/composables/useNotifications";
import { useEquipment } from "@/composables/tasks/useEquipment";
import TaskCompletionBlockedModal from "@/components/v2/Tasks/Task/TaskCompletionBlockedModal.vue";

// ===================================================================
// Composables
// ===================================================================
const router = useRouter();
const { activeTask } = useTask();
const { configurations } = useVaccination();
const { getConfigurationTags } = useVaccinationConfigurations();
const { getVaccinationRecordsByConfigKey } = useRecord();
const { getAverageWeightByConfigKeyVaccinationRecords } = useWeight();
const { showTaskCompletionSuccess, showError } = useNotifications();
const { getWeightIdCompletionValidation } = useEquipment();

// ===================================================================
// State
// ===================================================================
const activeConfigKey = ref<string>('');
const vaccinationRevisionModal = ref<boolean>(false);
const selectedEntry = ref<VaccinationProgressEntry | null>(null);
const selectedEntryIndex = ref<number>(-1);
const isFinishing = ref<boolean>(false);
const completionBlockModal = ref<boolean>(false);

// ===================================================================
// Actions
// ===================================================================
// Go back
const goBack = () => router.go(-1);

// Open revisions modal
const openRevisionsModal = (entry: VaccinationProgressEntry, entryIndex: number) => {
  vaccinationRevisionModal.value = true;
  selectedEntry.value = entry;
  selectedEntryIndex.value = entryIndex;
}

// Finish task handler
const handleFinishTask = async () => {
    if (!activeTask.value) return;

    const validation = getWeightIdCompletionValidation();
    if (!validation.isValid) {
      completionBlockModal.value = true;
      return;
    }
    
    isFinishing.value = true;
    try {
        await TasksService.finishTask(activeTask.value);
        showTaskCompletionSuccess(activeTask.value.title);
    } catch (error) {
        console.error('Error completing vaccination task:', error);
        showError("Task Completion Failed", "Failed to complete the vaccination task. Please try again.");
    } finally {
        isFinishing.value = false;
    }
}

// ===================================================================
// Computed
// ===================================================================
const vaccinationRecords = computed<VaccinationProgressEntry[] | []>(() => getVaccinationRecordsByConfigKey(activeConfigKey.value));
const averageWeight = computed<number | string>(() => getAverageWeightByConfigKeyVaccinationRecords(activeConfigKey.value));
const completionValidation = computed(() => getWeightIdCompletionValidation());
const getDestinationTankName = (config: any): string => {
  const destinationTankName = config?.destinationTank?.name || config?.destinationTank?.label;
  if (destinationTankName) return destinationTankName;
  return config?.tank?.name || config?.tank?.label || 'No destination tank';
};

// ===================================================================
// Watchers
// ===================================================================
// Watch for configurations composable state change
watch(
    () => configurations.value,
    (newConfigurations) => {
      if (!activeConfigKey.value && newConfigurations.length > 0) {
        activeConfigKey.value = newConfigurations[0].key;
      }
    },
    { immediate: true }
);
</script>

<template>
  <div class="flex flex-col h-full justify-between">
    <div class="space-y-6">
      <!-- Header -->
      <div class="flex justify-between items-center">
        <h1 class="text-2xl font-bold text-gray-800">IVP Administration Records</h1>
        <button
            @click="goBack"
            class="text-gray-600 hover:text-gray-800 flex items-center gap-1 text-sm"
        >
          <svg xmlns="http://www.w3.org/2000/svg" class="h-4 w-4" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.2" stroke-linecap="round" stroke-linejoin="round">
            <path d="M19 12H5M12 19l-7-7 7-7"/>
          </svg>
          Back
        </button>
      </div>
      <span class="w-full bg-secondary h-[1px] block"></span>

      <!-- Stats Cards -->
      <div class="grid grid-cols-3 gap-4">
        <div class="bg-white p-4 rounded-lg shadow-sm border border-gray-200">
          <h3 class="text-sm font-medium text-gray-500 mb-1">Total Administered IVPs</h3>
          <p class="text-2xl font-bold text-gray-800">
            {{ vaccinationRecords.reduce((sum, record) => sum + (record.vaccinated || 0), 0) }}
          </p>
        </div>
        <div class="bg-white p-4 rounded-lg shadow-sm border border-gray-200">
          <h3 class="text-sm font-medium text-gray-500 mb-1">Average Weight <span class="text-xs">(grams)</span></h3>
          <p class="text-2xl font-bold text-gray-800">
            {{ averageWeight }}
          </p>
        </div>
        <div class="bg-white p-4 rounded-lg shadow-sm border border-gray-200">
          <h3 class="text-sm font-medium text-gray-500 mb-1">Total Records</h3>
          <p class="text-2xl font-bold text-gray-800">{{ vaccinationRecords.length }}</p>
        </div>
      </div>
      <!-- Configuration Tabs -->
      <div class="bg-white rounded-lg shadow-sm border border-gray-200 overflow-hidden">
        <!-- Tab navigation -->
        <div class="flex overflow-x-auto border-b border-gray-200">
          <template v-for="(config, index) in configurations" :key="config.key">
            <button
                @click="activeConfigKey = config.key"
                class="px-4 py-2 text-sm font-medium whitespace-nowrap flex items-center gap-1"
                :class="activeConfigKey === config.key
                ? 'text-primary border-b-2 border-primary'
                : 'text-gray-500 hover:text-gray-700 hover:bg-gray-100'"
            >
              <div class="flex flex-col items-start gap-1">
                <span class="text-xs font-semibold">
                  {{ getDestinationTankName(config) }}
                </span>
                <div class="flex flex-wrap gap-1">
                  <span
                    v-for="tag in getConfigurationTags(config)"
                    :key="tag.key"
                    class="inline-block px-2 py-0.5 text-xs font-medium rounded-full"
                    :style="{
                      backgroundColor: tag.backgroundColor || '#e5e7eb',
                      color: tag.textColor || '#374151'
                    }"
                  >
                    {{ tag.title }}
                  </span>
                  <span v-if="getConfigurationTags(config).length === 0" class="text-gray-400">No tags</span>
                </div>
              </div>
            </button>
            <div
                v-if="index < configurations.length - 1"
                class="h-6 w-px bg-gray-300 self-center"
            ></div>
          </template>
        </div>

        <!-- Table -->
        <div class="overflow-x-auto">
          <div v-if="!vaccinationRecords.length" class="text-center py-8 text-gray-500">
            No vaccination records for this configuration
          </div>

          <table v-else class="min-w-full">
            <thead class="bg-gray-50">
            <tr>
              <th class="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                #
              </th>
              <th class="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                Comment
              </th>
              <th class="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                Administered IVPs
              </th>
              <th class="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                Weight
              </th>
              <th class="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                No. Revisions
              </th>
              <th class="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
              Actions
              </th>
            </tr>
            </thead>
            <tbody class="divide-y divide-gray-200">
            <tr v-for="(record, index) in vaccinationRecords" :key="index" class="hover:bg-gray-50">
              <td class="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                {{ index + 1 }}
              </td>
              <td class="px-6 py-4 text-sm text-gray-500 max-w-xs">
                {{ record.comment || 'No comment' }}
              </td>
              <td class="px-6 py-4 whitespace-nowrap text-sm font-medium text-gray-900">
                {{ record.vaccinated || 0 }}
              </td>
              <td class="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                {{ record.weight || 'N/A' }}
              </td>
              <td class="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                {{ record.revisions?.length || 0 }}
              </td>
              <td>
                <button
                    @click="openRevisionsModal(record, index)"
                    class="px-4 py-2 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-md hover:bg-gray-100 transition-colors duration-200 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-primary"
                >
                  Add Revision
                </button>
              </td>
            </tr>
            </tbody>
          </table>
        </div>
      </div>
    </div>

    <VaccinationRevisionModal
        :entry="selectedEntry"
        :configKey="activeConfigKey"
        :entryIndex="selectedEntryIndex"
        :isOpen="vaccinationRevisionModal"
        v-on:close="() => vaccinationRevisionModal = false"
    />
    <TaskCompletionBlockedModal
        :show="completionBlockModal"
        :reason="completionValidation.reason"
        @close="completionBlockModal = false"
    />

    <!-- Footer -->
    <FooterActions>
      <template #middle>
        <button
            @click="goBack"
            class="w-full"
        >
          Back
        </button>
      </template>
      <template #end>
        <div class="w-full text-center">
          <button
              @click="handleFinishTask"
              :disabled="isFinishing"
              class="w-full"
              :class="{ 'opacity-50 cursor-not-allowed': isFinishing }"
          >
            Finish Task
          </button>
        </div>
      </template>
    </FooterActions>
  </div>
</template>
